import {
  ORDER_FLAGS_LOADED,
  ORDER_FLAG_CHANGE_REQUESTED,
  ORDER_FLAG_CHANGE_SETTLED,
  ORDER_FLAG_CHANGE_FAILED,
  ORDER_FLAGS_RESET,
  ORDER_FLAGS_SCOPE_CHECKED,
  type OrderFlagsAction,
} from '../actions/orderFlags';

export type OrderFlagEntry = {
  orderId: string;
  entityType: string;
  entityId: string | null;
};

export type OrderFlagStateEntry = OrderFlagEntry & {
  flagged: boolean;
  changedAt: string;
};

/** De quién son las marcas: de la persona o de su grupo (spec banderitas por grupo). */
export type OrderFlagScope = {
  kind: 'USER' | 'GROUP';
  groupId: string | null;
  groupName: string | null;
  /** Cambia cada vez que cambia el conjunto de marcas o la pertenencia al grupo. */
  stamp: string;
};

export type OrderFlagsState = {
  /** `${userId}:${tenantId}` dueño de estas marcas. */
  ownerKey: string | null;
  /** Lo que el servidor dijo en la última carga/confirmación. */
  byOrderId: Record<string, OrderFlagEntry>;
  /** Cambios locales aún sin confirmar (en vuelo o encolados sin red). */
  pending: Record<string, { flagged: boolean; changedAt: string }>;
  /**
   * Cuándo (ms) confirmó el servidor el último cambio de cada orden. Una carga
   * pedida antes no lo incluye y no debe pisarlo.
   */
  settledAt: Record<string, number>;
  loadedAt: number | null;
  /**
   * Último ámbito confirmado por el servidor. Un estado persistido de la
   * versión anterior no lo trae: leer siempre con `?? null`.
   */
  scope: OrderFlagScope | null;
  /** Cuándo (ms) se comprobó el ámbito por última vez. */
  scopeCheckedAt: number | null;
};

/** Un pendiente más viejo que esto se descarta al cargar. */
export const PENDING_FLAG_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export const initialOrderFlagsState: OrderFlagsState = {
  ownerKey: null,
  byOrderId: {},
  pending: {},
  settledAt: {},
  loadedAt: null,
  scope: null,
  scopeCheckedAt: null,
};

const toMs = (iso: string) => Date.parse(iso);

export default function orderFlags(
  state: OrderFlagsState = initialOrderFlagsState,
  action: OrderFlagsAction | { type: string }
): OrderFlagsState {
  const a = action as OrderFlagsAction;
  switch (a.type) {
    case ORDER_FLAGS_LOADED: {
      const { ownerKey, entries, loadedAt, requestedAt } = a.payload;
      const byOrderId: Record<string, OrderFlagEntry> = {};
      for (const e of entries) byOrderId[e.orderId] = e;

      // Otro dueño: ni los pendientes ni lo confirmado son suyos.
      const sameOwner = state.ownerKey === ownerKey;
      const basePending = sameOwner ? state.pending : {};
      // `?? {}`: un estado persistido antes de existir `settledAt` no lo trae.
      const baseSettledAt = sameOwner ? state.settledAt ?? {} : {};

      // Confirmado DESPUÉS de pedir la carga: su foto no lo sabe, manda lo
      // actual (que es lo que dijo el servidor al confirmar). Lo confirmado
      // antes ya viene en la foto y se olvida.
      const settledAt: OrderFlagsState['settledAt'] = {};
      for (const [orderId, at] of Object.entries(baseSettledAt)) {
        if (at < requestedAt) continue;
        settledAt[orderId] = at;
        const current = state.byOrderId[orderId];
        if (current) byOrderId[orderId] = current;
        else delete byOrderId[orderId];
      }

      const pending: OrderFlagsState['pending'] = {};
      for (const [orderId, p] of Object.entries(basePending)) {
        // Demasiado viejo (o fecha ilegible): la cola lo tiró sin avisar.
        const age = loadedAt - toMs(p.changedAt);
        if (!Number.isFinite(age) || age > PENDING_FLAG_MAX_AGE_MS) continue;
        // El servidor ya coincide con lo pendiente: ya no hace falta.
        if (p.flagged === (orderId in byOrderId)) continue;
        pending[orderId] = p;
      }
      // El ámbito sobrevive a la carga (solo del mismo dueño); `?? null` por
      // si el estado persistido es anterior a que existiera.
      return {
        ownerKey,
        byOrderId,
        pending,
        settledAt,
        loadedAt,
        scope: sameOwner ? state.scope ?? null : null,
        scopeCheckedAt: sameOwner ? state.scopeCheckedAt ?? null : null,
      };
    }
    case ORDER_FLAG_CHANGE_REQUESTED: {
      const { orderIds, flagged, changedAt } = a.payload;
      const pending = { ...state.pending };
      for (const id of orderIds) pending[id] = { flagged, changedAt };
      return { ...state, pending };
    }
    case ORDER_FLAG_CHANGE_SETTLED: {
      const byOrderId = { ...state.byOrderId };
      const pending = { ...state.pending };
      const settledAt = { ...(state.settledAt ?? {}) };
      for (const s of a.payload.states) {
        settledAt[s.orderId] = a.payload.settledAt;
        if (s.flagged) {
          byOrderId[s.orderId] = {
            orderId: s.orderId,
            entityType: s.entityType,
            entityId: s.entityId,
          };
        } else {
          delete byOrderId[s.orderId];
        }
        // Solo se limpia lo pendiente igual o más viejo que la respuesta.
        const p = pending[s.orderId];
        if (p && toMs(p.changedAt) <= toMs(s.changedAt)) {
          delete pending[s.orderId];
        }
      }
      return { ...state, byOrderId, pending, settledAt };
    }
    case ORDER_FLAG_CHANGE_FAILED: {
      const { orderIds, changedAt } = a.payload;
      const pending = { ...state.pending };
      for (const id of orderIds) {
        if (pending[id]?.changedAt === changedAt) delete pending[id];
      }
      return { ...state, pending };
    }
    case ORDER_FLAGS_SCOPE_CHECKED: {
      const { ownerKey, scope, checkedAt } = a.payload;
      if (state.ownerKey !== ownerKey) return state;
      return { ...state, scope, scopeCheckedAt: checkedAt };
    }
    case ORDER_FLAGS_RESET:
      // Vacío pero ya a nombre del dueño nuevo: así lo que marque sin red
      // sobrevive a la siguiente carga, y solo otro dueño lo borra.
      return { ...initialOrderFlagsState, ownerKey: a.payload.ownerKey };
    default:
      return state;
  }
}
